import React from 'react';
import PropTypes from 'prop-types';
import { Container } from 'semantic-ui-react';
import { Award } from 'lucide-react';
import TeaserImages from '../Helpers/TeaserImages';
import RelatedItemsDisplay from '../Helpers/RelatedItemsDisplay';
import Sections from '../Helpers/Sections';
import DefaultViewWithoutTitle from '../../Custom/DefaultViewWithoutTitle';
import { hasImage, vocabTitle } from '../../../helpers/content';
import { flattenRichTextToAppURL } from '../../../helpers/richText';

const CompetitionView = (props) => {
  const { content } = props;
  const teaserImage1 = content.teaser_image1;
  const teaserImage2 = content.teaser_image2;
  const hasImages = hasImage(teaserImage1) || hasImage(teaserImage2);

  const typeLabel = vocabTitle(content.type_of_competition);
  const yearLabel = vocabTitle(content.competition_year);

  const hasInfo = content.competition_location || yearLabel;

  const hasReport =
    content.competition_report?.data &&
    content.competition_report.data !== '<p></p>';

  const hasPublications =
    content.related_publications?.length > 0 &&
    !content.hide_related_publications;
  const hasTopics =
    content.related_topics?.length > 0 && !content.hide_related_topics;

  return (
    <Container id="view-wrapper" className="CompetitionView">
      {/* Header */}
      <div className="view-header">
        <div className="view-header__badges">
          {typeLabel && (
            <span className="view-header__subtitle">{typeLabel}</span>
          )}
        </div>
        <h2 className="view-header__title">
          {content.title || content.id}
          {content.is_best_result && (
            <Award
              size={22}
              className="cards__title-icon"
              aria-label="Best Result / Award"
            />
          )}
        </h2>
        {/* No description here. `description` is a listing field - the one
            line that goes on a card and into a search result - and repeating
            it as the first paragraph of the page it summarises says the same
            thing twice. The full view has its own content. */}
        <div className="view-header__divider" />
      </div>
      <Sections>
        {/* Teaser Images */}
        {hasImages && <TeaserImages content={content} />}

        {/* Info */}
        {hasInfo && (
          <>
            <div className="view-meta-grid">
              <div className="view-meta-grid__col">
                {content.competition_location && (
                  <div className="view-section">
                    <h3 className="view-section__label">Location:</h3>
                    <p className="view-section__value">
                      {content.competition_location}
                    </p>
                  </div>
                )}
              </div>
              <div className="view-meta-grid__col">
                {yearLabel && (
                  <div className="view-section">
                    <h3 className="view-section__label">Year:</h3>
                    <p className="view-section__value">{yearLabel}</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Competition Report */}
        {hasReport && (
          <>
            <div className="view-section">
              <h3 className="view-section__label">Competition Report:</h3>
              <div
                className="view-section__richtext"
                dangerouslySetInnerHTML={{
                  __html: flattenRichTextToAppURL(
                    content.competition_report.data,
                  ),
                }}
              />
            </div>
          </>
        )}

        {/* Related Publications */}
        {hasPublications && (
          <>
            <div className="view-section">
              <h3 className="view-section__label">Related Publications:</h3>
              <RelatedItemsDisplay
                items={content.related_publications}
                mode={content.related_publications_display}
                kind="plain"
                fieldKey={`${content['@id']}-related_publications`}
              />
            </div>
          </>
        )}

        {/* Related Topics */}
        {hasTopics && (
          <>
            <div className="view-section">
              <h3 className="view-section__label">Related Topics:</h3>
              <RelatedItemsDisplay
                items={content.related_topics}
                mode={content.related_topics_display}
                kind="plain"
                fieldKey={`${content['@id']}-related_topics`}
              />
            </div>
          </>
        )}
      </Sections>

      {/* The block-rendered part of the page */}
      <DefaultViewWithoutTitle {...props} />
    </Container>
  );
};

CompetitionView.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    id: PropTypes.string,
    description: PropTypes.string,
    type_of_competition: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ title: PropTypes.string }),
    ]),
    competition_year: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ title: PropTypes.string }),
    ]),
    competition_location: PropTypes.string,
    is_best_result: PropTypes.bool,
    competition_report: PropTypes.shape({ data: PropTypes.string }),
    teaser_image1: PropTypes.object,
    teaser_image1_caption: PropTypes.string,
    teaser_image1_copyright: PropTypes.string,
    teaser_image1_copyright_black_font: PropTypes.bool,
    teaser_image2: PropTypes.object,
    teaser_image2_caption: PropTypes.string,
    teaser_image2_copyright: PropTypes.string,
    teaser_image2_copyright_black_font: PropTypes.bool,
    items: PropTypes.arrayOf(PropTypes.object),
    blocks_layout: PropTypes.shape({
      items: PropTypes.arrayOf(PropTypes.string),
    }),
    hide_related_publications: PropTypes.bool,
    hide_related_topics: PropTypes.bool,
    related_publications_display: PropTypes.string,
    related_topics_display: PropTypes.string,
    related_publications: PropTypes.arrayOf(
      PropTypes.shape({ '@id': PropTypes.string, title: PropTypes.string }),
    ),
    related_topics: PropTypes.arrayOf(
      PropTypes.shape({ '@id': PropTypes.string, title: PropTypes.string }),
    ),
  }).isRequired,
};

export default CompetitionView;
